import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { campaignsApi } from "../../api/campaigns";
import { ResidentInput } from "../../types";

export const CreateCampaign: React.FC = () => {
  const navigate = useNavigate();
  const [buildingAddress, setBuildingAddress] = useState("");
  const [buildingId, setBuildingId] = useState("");
  const [isPpe, setIsPpe] = useState(false);
  const [residents, setResidents] = useState<ResidentInput[]>([
    { external_id: "", email: "", name: "", object_name: "" },
  ]);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const createMutation = useMutation({
    mutationFn: campaignsApi.createCampaign,
    onSuccess: (data) => {
      setMessage({
        type: "success",
        text: `Campaign created successfully! ${data.created_count} residents added.`,
      });
      setTimeout(() => {
        navigate(`/admin/campaign/${data.campaign_id}`);
      }, 2000);
    },
    onError: (error: unknown) => {
      let errorMessage = "Failed to create campaign";
      if (axios.isAxiosError(error)) {
        errorMessage = error.response?.data?.detail || errorMessage;
      }
      setMessage({
        type: "error",
        text: errorMessage,
      });
    },
  });

  const addResident = () => {
    setResidents([...residents, { external_id: "", email: "", name: "", object_name: "" }]);
  };

  const removeResident = (index: number) => {
    if (residents.length > 1) {
      setResidents(residents.filter((_, i) => i !== index));
    }
  };

  const updateResident = (
    index: number,
    field: "external_id" | "email" | "name" | "object_name",
    value: string
  ) => {
    const updated = [...residents];
    updated[index][field] = value;
    setResidents(updated);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Filter out empty residents - email and object_name are optional, but external_id and name are required
    const validResidents = residents
      .filter((r) => r.external_id && r.name)
      .map((r) => ({
        ...r,
        email: r.email && r.email.trim() ? r.email.trim() : undefined,
        object_name: r.object_name && r.object_name.trim() ? r.object_name.trim() : undefined,
      }));

    if (validResidents.length === 0) {
      setMessage({
        type: "error",
        text: "Please add at least one resident with external_id and name",
      });
      return;
    }

    createMutation.mutate({
      building_address: buildingAddress,
      building_id: buildingId,
      is_ppe: isPpe,
      residents: validResidents,
    });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-primary">Naef</h1>
              <span className="ml-4 text-gray-600">Create Campaign</span>
            </div>
            <button onClick={() => navigate("/admin/dashboard")} className="btn btn-secondary">
              Back to Dashboard
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="card">
          <h2 className="text-2xl font-bold mb-6">New Data Collection Campaign</h2>

          {message && (
            <div
              className={`mb-4 px-4 py-3 rounded ${
                message.type === "success"
                  ? "bg-green-100 border border-green-400 text-green-700"
                  : "bg-red-100 border border-red-400 text-red-700"
              }`}
            >
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Building Address
              </label>
              <input
                type="text"
                value={buildingAddress}
                onChange={(e) => setBuildingAddress(e.target.value)}
                className="input"
                required
                placeholder="e.g., 123 Main Street, Geneva"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Building ID</label>
              <input
                type="text"
                value={buildingId}
                onChange={(e) => setBuildingId(e.target.value)}
                className="input"
                required
                placeholder="e.g., BLDG-001"
              />
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_ppe"
                checked={isPpe}
                onChange={(e) => setIsPpe(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300"
              />
              <label htmlFor="is_ppe" className="text-sm font-medium text-gray-700 cursor-pointer">
                PPE (Propriété Par Étages / Co-ownership)
              </label>
            </div>

            <div>
              <div className="flex justify-between items-center mb-3">
                <label className="block text-sm font-medium text-gray-700">Residents</label>
                <button
                  type="button"
                  onClick={addResident}
                  className="text-sm text-primary hover:text-primary-dark font-medium"
                >
                  + Add Resident
                </button>
              </div>

              <div className="space-y-3">
                {residents.map((resident, index) => (
                  <div key={index} className="flex gap-3 items-start">
                    <div className="flex-1">
                      <input
                        type="text"
                        placeholder="External ID"
                        value={resident.external_id}
                        onChange={(e) => updateResident(index, "external_id", e.target.value)}
                        className="input"
                        required
                      />
                    </div>
                    <div className="flex-1">
                      <input
                        type="text"
                        placeholder="Name"
                        value={resident.name}
                        onChange={(e) => updateResident(index, "name", e.target.value)}
                        className="input"
                        required
                      />
                    </div>
                    <div className="flex-1">
                      <input
                        type="email"
                        placeholder="Email (optional)"
                        value={resident.email || ""}
                        onChange={(e) => updateResident(index, "email", e.target.value)}
                        className="input"
                      />
                    </div>
                    <div className="flex-1">
                      <input
                        type="text"
                        placeholder="Object Name (optional, e.g., Appart 1er étage)"
                        value={resident.object_name}
                        onChange={(e) => updateResident(index, "object_name", e.target.value)}
                        className="input"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeResident(index)}
                      disabled={residents.length === 1}
                      className={`px-3 py-2 rounded-md ${
                        residents.length === 1
                          ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                          : "bg-red-100 text-red-600 hover:bg-red-200"
                      }`}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex space-x-4 pt-4">
              <button type="submit" disabled={createMutation.isPending} className="btn btn-primary">
                {createMutation.isPending ? "Creating..." : "Create Campaign"}
              </button>
              <button
                type="button"
                onClick={() => navigate("/admin/dashboard")}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
